import React from 'react';
import { cx } from '@linaria/atomic';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import type { CommonHTMLAttributes } from '@frontend/types';
import { Text } from '../text';
import { CONTENT_LOADER, CONTENT_LOADER_TEXT } from './loaders.styles';
import { SpinnerSize, SpinningLoader } from './spinning-loader';

export type ContentLoaderProps = CommonHTMLAttributes & {
  backgroundOpacity?: number;
  children?: React.ReactNode;
  message?: string;
  show?: boolean;
  size?: SpinnerSize;
} & MotionProps;

const defaultOpacity = 0.85;

/**
 * Loading overlay for a content block. Will cover the entire area of the closest positioned ancestor element.
 * @param {number} [props.backgroundOpacity] Optional setting to control background opacity. Must be a number between (or including) 0-1
 * @param {string} [props.message] Optional message to show below the loader.
 * @param {boolean} [props.show] Toggles loader visibility.
 */
export const ContentLoader = ({
  backgroundOpacity = defaultOpacity,
  children,
  message,
  show,
  size,
  className,
  style,
  ...rest
}: ContentLoaderProps) => {
  let opacity = backgroundOpacity;
  if (!(opacity > 0 && opacity < 1)) opacity = defaultOpacity;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={cx(CONTENT_LOADER, className)}
          style={{ background: `rgba(255, 255, 255, ${opacity})`, ...style }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, pointerEvents: 'none' }}
          transition={{ duration: 0.2 }}
          {...rest}
        >
          <SpinningLoader key='loader' size={size} />
          {!!message && (
            <Text key='message' textAlign='center' className={CONTENT_LOADER_TEXT}>
              {message}
            </Text>
          )}
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
